export type ManualCalloutType = 'tip' | 'warning' | 'know';

export interface ManualCallout {
  type: ManualCalloutType;
  title?: string;
  text: string;
}

export interface ManualFigureAnnotation {
  label: string;
  x: number;
  y: number;
  text: string;
}

export interface ManualFigure {
  src: string;
  caption: string;
  description?: string;
  alt?: string;
  annotations?: ManualFigureAnnotation[];
}

export interface ManualSubsection {
  title: string;
  text?: string;
  steps?: string[];
  items?: string[];
}

export interface ManualItem {
  title: string;
  module: string;
  path: string;
  routeName?: string;
  summary: string;
  example: string;
  advice: string;
  objective?: string;
  scenario?: string;
  steps?: string[];
  impacts?: string[];
  commonErrors?: string[];
  callouts?: ManualCallout[];
  figures?: ManualFigure[];
  subsections?: ManualSubsection[];
  keywords?: string[];
}

const screenshot = (name: string): string => `/manual/screenshots/${name}.svg`;

export const adminManualItems: ManualItem[] = [
  {
    title: '1. Présentation du logiciel',
    module: 'Présentation',
    path: 'Suite241 Gestion Commerciale',
    summary: 'Suite241 Gestion Commerciale aide une boutique à suivre les produits, les achats, les ventes, la caisse, le stock, les clients et les rapports au même endroit.',
    example: 'Exemple: une supérette peut enregistrer ses achats de boissons le matin, vendre à la caisse toute la journée, puis contrôler les espèces et les ruptures le soir.',
    advice: 'Commencez par les tâches simples: créer les produits, ouvrir la caisse, vendre, puis consulter les rapports.',
    objective: 'Comprendre le rôle du logiciel avant de l’utiliser au quotidien.',
    scenario: 'Je gère une boutique de quartier avec plusieurs rayons et je veux savoir ce que j’ai vendu, ce qu’il me reste en stock et combien la caisse doit contenir.',
    steps: [
      'Paramétrez d’abord les informations de l’entreprise et le site.',
      'Créez les rayons, catégories, fournisseurs, clients et produits.',
      'Enregistrez les achats pour faire entrer le stock.',
      'Utilisez la caisse pour vendre et imprimer les tickets.',
      'Contrôlez la fin de journée, le stock et les rapports.',
    ],
    impacts: [
      'Une vente validée diminue le stock et augmente le chiffre d’affaires.',
      'Un achat validé augmente le stock.',
      'Un inventaire validé corrige le stock selon le comptage réel.',
      'Une clôture de caisse fixe le montant théorique de la journée.',
    ],
    callouts: [
      {
        type: 'know',
        title: 'Bon à savoir',
        text: 'Le logiciel garde l’historique des actions importantes. Il vaut mieux désactiver une donnée utilisée que la supprimer.',
      },
      {
        type: 'tip',
        title: 'Astuce',
        text: 'Travaillez par routine: achats avant mise en rayon, ventes pendant la journée, contrôle caisse et ruptures le soir.',
      },
    ],
    subsections: [
      {
        title: 'Profils utilisateurs',
        items: [
          'Gérant: configure l’entreprise, les produits, les achats, la caisse, les rapports et les utilisateurs.',
          'Caissier: vend, imprime les tickets, compte certains inventaires et clôture sa journée.',
          'Responsable stock: suit les entrées, sorties, mouvements, ruptures et inventaires.',
          'Super administrateur: supervise plusieurs sites et les paramètres avancés.',
        ],
      },
    ],
  },
  {
    title: '2. Première connexion et changement du mot de passe',
    module: 'Première connexion',
    path: 'Backoffice > Connexion · /backoffice/login',
    routeName: 'Login',
    summary: 'Connectez-vous avec le nom d’utilisateur et le mot de passe reçus, puis changez le mot de passe temporaire si l’application le demande.',
    example: 'Exemple: le nouveau caissier reçoit “caissier1” et un mot de passe provisoire. À la première connexion, il doit créer son propre mot de passe.',
    advice: 'Un mot de passe doit rester personnel. Ne partagez pas le compte d’un caissier entre plusieurs personnes.',
    objective: 'Entrer dans le logiciel de façon sécurisée.',
    scenario: 'Je viens de recevoir mes identifiants et je veux accéder à mon espace de travail.',
    steps: [
      'Ouvrez l’adresse de Suite241 dans le navigateur.',
      'Saisissez votre nom d’utilisateur.',
      'Saisissez votre mot de passe.',
      'Cliquez sur “Se connecter”.',
      'Si l’écran de sécurité apparaît, saisissez l’ancien mot de passe puis votre nouveau mot de passe.',
    ],
    impacts: [
      'Le compte connecté détermine les menus visibles.',
      'Les ventes, modifications et clôtures seront rattachées à cet utilisateur.',
    ],
    commonErrors: [
      'Nom d’utilisateur mal écrit: vérifiez les majuscules, espaces et tirets.',
      'Mot de passe oublié: demandez au gérant de réinitialiser le compte.',
      'Page qui reste sur le changement de mot de passe: rechargez l’application après avoir vérifié que le toast de succès s’affiche.',
    ],
    callouts: [
      {
        type: 'warning',
        title: 'Attention',
        text: 'Ne notez pas le mot de passe sur le comptoir. Utilisez un mot de passe connu seulement par l’utilisateur.',
      },
    ],
    figures: [
      {
        src: screenshot('login'),
        caption: 'Figure 1 : Page de connexion.',
        description: 'Le nom d’utilisateur et le mot de passe permettent d’ouvrir la session.',
        annotations: [
          { label: '1', x: 58, y: 47, text: 'Saisissez le nom d’utilisateur fourni par le gérant.' },
          { label: '2', x: 58, y: 61, text: 'Saisissez le mot de passe.' },
          { label: '3', x: 58, y: 75, text: 'Validez pour entrer dans l’application.' },
        ],
      },
      {
        src: screenshot('change-password'),
        caption: 'Figure 2 : Changement du mot de passe temporaire.',
        description: 'Cet écran protège les comptes créés automatiquement ou réinitialisés.',
        annotations: [
          { label: '1', x: 60, y: 43, text: 'Saisissez le mot de passe actuel.' },
          { label: '2', x: 60, y: 57, text: 'Choisissez un nouveau mot de passe.' },
          { label: '3', x: 60, y: 72, text: 'Confirmez puis continuez.' },
        ],
      },
    ],
  },
  {
    title: '3. Découvrir l’interface',
    module: 'Première connexion',
    path: 'Backoffice > Tableau de bord · /backoffice/tableau-de-bord',
    routeName: 'Dashboard',
    summary: 'Le tableau de bord donne une vue rapide sur les ventes, le stock, les ruptures, les achats et les activités importantes.',
    example: 'Exemple: sélectionnez “30 derniers jours” pour comparer les ventes par caissier et repérer les meilleures performances.',
    advice: 'Commencez chaque journée par les alertes stock et les ventes récentes pour décider les priorités.',
    objective: 'Savoir où trouver les informations principales.',
    scenario: 'J’ouvre le logiciel le matin et je veux voir si la boutique a des produits en rupture ou des ventes importantes à contrôler.',
    steps: [
      'Utilisez la barre de navigation pour passer d’un module à l’autre.',
      'Ouvrez les menus déroulants pour accéder aux sous-pages.',
      'Utilisez les filtres dans les tableaux pour limiter les résultats.',
      'Cliquez sur une ligne ou sur “Voir” pour ouvrir le détail.',
    ],
    impacts: [
      'Le tableau de bord ne modifie pas les données. Il sert à contrôler et décider.',
      'Les chiffres dépendent du site courant et de la période choisie.',
    ],
  },
  {
    title: 'Afficher, masquer et réinitialiser les filtres',
    module: 'Navigation',
    path: 'Backoffice > Bouton Filtres sur les listes',
    summary: 'Les filtres des listes sont masqués par défaut. Cliquez sur le bouton “Filtres” pour afficher ou masquer le panneau de filtres.',
    example: 'Exemple: ouvrez Produits, cliquez sur “Filtres”, choisissez un rayon, puis cliquez sur “Filtrer”. Vous pouvez refermer le panneau: le rayon reste appliqué.',
    advice: 'Masquer le panneau ne désactive pas le filtre. Utilisez toujours “Réinitialiser” pour revenir à la liste complète.',
    objective: 'Retrouver rapidement une donnée dans une longue liste.',
    scenario: 'Je veux retrouver toutes les ventes d’hier payées en espèces sans parcourir toutes les pages.',
    steps: [
      'Cliquez sur “Filtres”.',
      'Renseignez les champs utiles.',
      'Cliquez sur “Filtrer”.',
      'Cliquez sur “Réinitialiser” pour enlever les critères.',
    ],
    callouts: [
      {
        type: 'know',
        text: 'Dans les tableaux modernes, vous pouvez aussi aller directement à une page grâce aux boutons de pagination.',
      },
    ],
  },
  {
    title: '4. Paramétrage initial de l’entreprise',
    module: 'Paramétrage initial',
    path: 'Backoffice > Paramètres > Informations boutique · /backoffice/parametres/boutique',
    routeName: 'ShopInfo',
    summary: 'Renseignez le nom commercial, les coordonnées, le logo, les préférences de caisse, le billetage et les informations affichées sur les documents.',
    example: 'Exemple: “Alimentation Chez Amina” ajoute son téléphone, son adresse et active le billetage si le caissier doit compter les billets en fin de journée.',
    advice: 'Vérifiez ces informations avant d’imprimer les premiers tickets ou factures.',
    objective: 'Préparer l’application pour votre boutique.',
    scenario: 'Je viens d’installer le logiciel dans ma boutique et je souhaite enregistrer les informations de mon entreprise.',
    steps: [
      'Ouvrez Paramètres puis Informations boutique.',
      'Renseignez le nom, le téléphone, l’adresse et les informations utiles.',
      'Choisissez si le billetage est obligatoire en fin de journée.',
      'Enregistrez les changements.',
    ],
    impacts: [
      'Les informations apparaissent sur les tickets, factures et documents.',
      'Si le billetage est désactivé, la clôture de journée devient plus simple et ne demande qu’un commentaire facultatif.',
    ],
    callouts: [
      {
        type: 'warning',
        text: 'Évitez de modifier les paramètres pendant une forte affluence en caisse.',
      },
    ],
    figures: [
      {
        src: screenshot('shop-settings'),
        caption: 'Figure 3 : Formulaire des informations de l’entreprise.',
        description: 'Les champs principaux identifient la boutique et contrôlent certains comportements de caisse.',
        annotations: [
          { label: '1', x: 33, y: 38, text: 'Nom et coordonnées de l’entreprise.' },
          { label: '2', x: 66, y: 53, text: 'Option billetage de fin de journée.' },
          { label: '3', x: 76, y: 78, text: 'Bouton d’enregistrement.' },
        ],
      },
    ],
  },
  {
    title: '5. Créer un produit',
    module: 'Produits',
    path: 'Backoffice > Stock > Produits > Nouveau produit · /backoffice/produits',
    routeName: 'Products',
    summary: 'Créez, importez et modifiez les produits actifs du site: prix, formule par lot, rayon, catégorie, code-barres, taxes, stock et vente avec stock négatif si autorisée.',
    example: 'Exemple: pour un produit à 500 F, activez une formule de 3 unités pour 1 000 F; 4 unités coûteront 1 500 F.',
    advice: 'Renseignez toujours le rayon, le prix de vente et les taxes applicables pour fiabiliser les rapports.',
    objective: 'Enregistrer un produit pour le vendre, suivre son stock et l’afficher en caisse.',
    scenario: 'Je viens de recevoir 24 bouteilles d’eau minérale et je souhaite les enregistrer.',
    steps: [
      'Ouvrez Stock puis Produits.',
      'Cliquez sur “Nouveau produit”.',
      'Renseignez le nom du produit.',
      'Laissez le SKU se préremplir depuis le nom, ou modifiez-le si nécessaire.',
      'Choisissez le rayon, la catégorie, le prix d’achat et le prix de vente.',
      'Si le produit se vend par formule, activez-la puis saisissez la quantité du lot et son prix total.',
      'Renseignez le stock initial si vous connaissez déjà la quantité.',
      'Enregistrez.',
    ],
    impacts: [
      'Le produit devient disponible dans les listes et en caisse s’il est actif.',
      'Un code-barres peut être généré automatiquement si vous n’en saisissez pas.',
      'Le stock initial sert de base aux ventes, achats et inventaires.',
      'En caisse, les lots complets utilisent le prix de la formule et le reliquat conserve le prix normal.',
      'Une promotion active ou saisie par le caissier reste prioritaire sur la formule.',
    ],
    commonErrors: [
      'Prix de vente oublié: le produit sera difficile à vendre correctement.',
      'Rayon non renseigné: la caisse sera moins facile à utiliser.',
      'Doublon produit: vérifiez d’abord la base de données produits.',
    ],
    callouts: [
      {
        type: 'know',
        text: 'La vente avec stock négatif doit rester réservée aux produits vendus en continu comme pain, galettes, beignets ou produits frais.',
      },
      {
        type: 'warning',
        text: 'Un produit sans stock ne peut pas être ajouté au panier en caisse, sauf si la vente avec stock négatif est autorisée.',
      },
      {
        type: 'know',
        text: 'La formule s’applique dans la caisse et apparaît dans le panier et sur le ticket; elle ne modifie pas les commandes de la boutique en ligne.',
      },
    ],
    figures: [
      {
        src: screenshot('product-form'),
        caption: 'Figure 4 : Formulaire d’ajout d’un produit.',
        description: 'Le champ Nom du produit est obligatoire. Le prix de vente correspond au prix payé par le client.',
        annotations: [
          { label: '1', x: 26, y: 32, text: 'Nom et SKU du produit.' },
          { label: '2', x: 62, y: 39, text: 'Rayon et catégorie.' },
          { label: '3', x: 42, y: 64, text: 'Prix et stock.' },
          { label: '4', x: 72, y: 74, text: 'Autorisation de vente avec stock négatif.' },
        ],
      },
    ],
  },
  {
    title: '6. Modifier, désactiver ou fusionner des produits',
    module: 'Produits',
    path: 'Backoffice > Stock > Produits · /backoffice/produits',
    routeName: 'Products',
    summary: 'La liste des produits permet de corriger une fiche, désactiver un produit qui n’est plus vendu ou fusionner plusieurs produits sources.',
    example: 'Exemple: si “Pain sucré” et “Pain doux” désignent le même article, fusionnez-les dans une seule fiche propre.',
    advice: 'Désactivez les produits qui ont déjà un historique au lieu de les supprimer.',
    objective: 'Garder une base produits claire et éviter les doublons.',
    scenario: 'Je remarque que le même savon a été créé deux fois avec deux noms différents.',
    steps: [
      'Ouvrez la liste des produits.',
      'Recherchez le produit à corriger.',
      'Ouvrez le détail ou la modification.',
      'Pour une fusion, cliquez sur “Fusionner”, sélectionnez au moins deux produits sources, puis renseignez la nouvelle fiche.',
      'Validez le récap et choisissez si les sources doivent être désactivées.',
    ],
    impacts: [
      'La modification change les ventes futures, pas les ventes déjà enregistrées.',
      'La fusion déplace le stock actuel et le cumul stock vers le nouveau produit.',
      'Chaque déplacement de stock crée un mouvement “Fusion de produits”.',
    ],
    figures: [
      {
        src: screenshot('product-list'),
        caption: 'Figure 5 : Liste des produits du site.',
        description: 'La recherche et les filtres aident à retrouver un produit avant modification.',
        annotations: [
          { label: '1', x: 23, y: 25, text: 'Recherche par mot-clé.' },
          { label: '2', x: 55, y: 25, text: 'Bouton Fusionner.' },
          { label: '3', x: 80, y: 58, text: 'Actions disponibles sur une ligne.' },
        ],
      },
    ],
  },
  {
    title: 'Fusionner des produits',
    module: 'Produits',
    path: 'Backoffice > Stock > Produits > Fusionner · /backoffice/produits',
    routeName: 'Products',
    summary: 'Créez un nouveau produit à partir de plusieurs produits sources. Le stock actuel et le cumul stock sont déplacés vers le nouveau produit.',
    example: 'Exemple: fusionnez “Pain blanc” et “Pain complet”, saisissez le nom, le rayon, la catégorie et le prix de vente du nouveau produit, puis validez le récap.',
    advice: 'Le SKU se préremplit depuis le nom si le champ est vide. Vous pouvez le modifier; le prix d’achat reste optionnel et les produits sources peuvent être désactivés après fusion.',
    objective: 'Nettoyer les doublons tout en conservant une trace des mouvements de stock.',
    scenario: 'Deux produits représentent la même boisson dans le stock et les rapports deviennent difficiles à lire.',
    steps: [
      'Cliquez sur “Fusionner”.',
      'Recherchez et ajoutez les produits sources.',
      'Renseignez le nom, le SKU, le rayon, la catégorie et le prix de vente du nouveau produit.',
      'Lisez le récap des stocks et cumul transférés.',
      'Gardez la case “Désactiver les produits source” cochée si vous ne voulez plus les vendre.',
      'Validez la fusion.',
    ],
    impacts: [
      'Les ventes et achats passés restent attachés aux anciens produits.',
      'Le nouveau produit reçoit le stock additionné.',
      'Les sources passent à stock zéro et cumul zéro.',
    ],
  },
  {
    title: '7. Utiliser la base de donnée produits',
    module: 'Base produits',
    path: 'Backoffice > Stock > Base de donnée · /backoffice/base-de-donnee-produits',
    routeName: 'ProductDatabase',
    summary: 'Consultez tous les produits connus par l’application, actifs ou non dans le site courant, puis activez ceux que votre site vend réellement.',
    example: 'Exemple: activez “Sucre blanc 1 kg” depuis la base au lieu de recréer une fiche doublon.',
    advice: 'Activez seulement les produits réellement vendus sur le site pour garder la caisse rapide.',
    objective: 'Réutiliser un produit existant dans la base globale.',
    scenario: 'J’ouvre une nouvelle boutique et je veux activer rapidement les mêmes produits que les autres sites.',
    steps: [
      'Ouvrez Base de donnée produits.',
      'Recherchez le produit.',
      'Cliquez sur la ligne pour ouvrir le mini dialogue.',
      'Choisissez “Activer sur ce site” ou “Activer dans tous les sites”.',
      'Vérifiez le résumé avant de continuer.',
    ],
    impacts: [
      'L’activation sur ce site rend le produit disponible uniquement dans la boutique courante.',
      'L’activation dans tous les sites crée ou réactive le produit dans tous les sites actifs.',
    ],
    figures: [
      {
        src: screenshot('product-database'),
        caption: 'Figure 6 : Base de donnée produits.',
        description: 'Le mini dialogue regroupe le récap et les actions du produit sélectionné.',
        annotations: [
          { label: '1', x: 26, y: 27, text: 'Recherche dans la base.' },
          { label: '2', x: 53, y: 51, text: 'Cliquez sur une ligne pour ouvrir le détail.' },
          { label: '3', x: 78, y: 54, text: 'Actions d’activation ou suppression.' },
        ],
      },
    ],
  },
  {
    title: '8. Gérer les fournisseurs',
    module: 'Fournisseurs',
    path: 'Backoffice > Achats > Fournisseurs · /backoffice/fournisseurs',
    routeName: 'Suppliers',
    summary: 'Centralisez les coordonnées fournisseurs et consultez les achats validés ainsi que les règlements.',
    example: 'Exemple: ajoutez le grossiste “Distribution Owendo” avec son téléphone et son adresse pour le retrouver à chaque commande.',
    advice: 'Gardez les contacts et délais de livraison à jour pour faciliter les réapprovisionnements.',
    objective: 'Retrouver facilement les grossistes et suivre les achats.',
    scenario: 'Je travaille avec plusieurs grossistes et je souhaite garder leurs coordonnées.',
    steps: [
      'Ouvrez Achats puis Fournisseurs.',
      'Cliquez sur “Nouveau fournisseur”.',
      'Renseignez le nom, le téléphone, l’adresse et les notes utiles.',
      'Enregistrez.',
      'Utilisez la fiche fournisseur pour consulter ses commandes et règlements.',
    ],
    impacts: [
      'Le fournisseur peut être sélectionné dans les commandes d’achat.',
      'Les rapports d’achats deviennent plus clairs.',
    ],
    figures: [
      {
        src: screenshot('suppliers'),
        caption: 'Figure 7 : Liste et fiche fournisseur.',
        description: 'Les coordonnées aident à passer commande rapidement.',
        annotations: [
          { label: '1', x: 24, y: 28, text: 'Recherche fournisseur.' },
          { label: '2', x: 74, y: 28, text: 'Création d’un fournisseur.' },
          { label: '3', x: 49, y: 64, text: 'Coordonnées et historique.' },
        ],
      },
    ],
  },
  {
    title: '9. Gérer les clients',
    module: 'Clients',
    path: 'Backoffice > Clients · /backoffice/clients',
    routeName: 'Clients',
    summary: 'Créez les clients, définissez le plafond dette, suivez la dette, les achats et les règlements.',
    example: 'Exemple: enregistrez “Mme Mba” comme cliente fidèle pour suivre ses achats et ses crédits.',
    advice: 'Évitez le crédit aux clients non identifiés et surveillez les dettes anciennes.',
    objective: 'Suivre les clients réguliers et les ventes à crédit.',
    scenario: 'Je souhaite enregistrer un client fidèle afin de suivre ses achats.',
    steps: [
      'Ouvrez Clients.',
      'Cliquez sur “Nouveau client”.',
      'Renseignez le nom, téléphone et informations utiles.',
      'Définissez un plafond de dette si le client peut acheter à crédit.',
      'Enregistrez.',
    ],
    impacts: [
      'Une vente à crédit augmente la dette du client.',
      'Un règlement client diminue cette dette.',
      'Les informations de pièce d’identité sont gérées uniquement par le backoffice autorisé.',
    ],
    figures: [
      {
        src: screenshot('clients'),
        caption: 'Figure 8 : Gestion des clients.',
        description: 'La fiche client permet de suivre dette, achats et coordonnées.',
        annotations: [
          { label: '1', x: 23, y: 27, text: 'Recherche client.' },
          { label: '2', x: 73, y: 27, text: 'Bouton de création.' },
          { label: '3', x: 48, y: 63, text: 'Dette et historique.' },
        ],
      },
    ],
  },
  {
    title: '10. Créer et valider une commande fournisseur',
    module: 'Achats',
    path: 'Backoffice > Achats > Commandes d’achats · /backoffice/achats',
    routeName: 'Purchases',
    summary: 'Créez un brouillon d’achat, ajoutez les produits, renseignez les quantités, puis validez à la réception pour mettre à jour le stock.',
    example: 'Exemple: commandez 10 cartons de boissons auprès du fournisseur, puis validez après contrôle de la marchandise.',
    advice: 'Validez uniquement lorsque les quantités physiques et les prix fournisseur sont confirmés.',
    objective: 'Faire entrer officiellement les marchandises en stock.',
    scenario: 'Je commande 10 cartons de boissons auprès de mon fournisseur.',
    steps: [
      'Ouvrez Achats puis Commandes d’achats.',
      'Cliquez sur “Nouvelle commande”.',
      'Choisissez le fournisseur.',
      'Ajoutez les produits commandés, les quantités et les prix d’achat.',
      'Enregistrez le brouillon si la marchandise n’est pas encore arrivée.',
      'À la réception, vérifiez les quantités puis validez la commande.',
    ],
    impacts: [
      'Un brouillon d’achat ne modifie pas le stock.',
      'Une commande validée augmente le stock des produits.',
      'Les produits achetés peuvent recevoir des étiquettes code-barres.',
    ],
    commonErrors: [
      'Valider avant réception: le stock devient trop élevé.',
      'Oublier un prix d’achat: les marges seront moins fiables.',
    ],
    figures: [
      {
        src: screenshot('purchase-form'),
        caption: 'Figure 9 : Création d’une commande fournisseur.',
        description: 'Ajoutez les produits, quantités et prix avant validation.',
        annotations: [
          { label: '1', x: 24, y: 33, text: 'Choix du fournisseur.' },
          { label: '2', x: 48, y: 55, text: 'Lignes de produits commandés.' },
          { label: '3', x: 77, y: 78, text: 'Enregistrement ou validation.' },
        ],
      },
      {
        src: screenshot('purchase-show'),
        caption: 'Figure 10 : Détail d’un achat validé.',
        description: 'Les actions restantes dépendent du statut de la commande.',
        annotations: [
          { label: '1', x: 31, y: 29, text: 'Statut de la commande.' },
          { label: '2', x: 50, y: 61, text: 'Produits reçus.' },
          { label: '3', x: 78, y: 61, text: 'Impression d’étiquettes.' },
        ],
      },
    ],
  },
  {
    title: '11. Consulter et corriger le stock',
    module: 'Stock',
    path: 'Backoffice > Stock > Mouvements · /backoffice/mouvements-stock',
    routeName: 'StockMovements',
    summary: 'Consultez les entrées, sorties, ajustements, transferts et fusions avec leurs motifs et sources.',
    example: 'Exemple: filtrez les mouvements d’un sac de riz pour comprendre pourquoi le stock est passé de 20 à 12.',
    advice: 'Chaque mouvement manuel doit avoir un motif clair pour faciliter l’audit.',
    objective: 'Comprendre pourquoi une quantité de stock a changé.',
    scenario: 'Je souhaite savoir combien de sacs de riz il me reste et pourquoi le stock a diminué.',
    steps: [
      'Ouvrez le détail du produit ou les mouvements de stock.',
      'Filtrez par produit, type ou période.',
      'Lisez le stock avant, le stock après et le motif.',
      'Si le stock physique est différent, utilisez un inventaire ou un ajustement autorisé.',
    ],
    impacts: [
      'Une vente diminue le stock.',
      'Un achat validé augmente le stock.',
      'Un inventaire validé applique une correction selon le comptage réel.',
      'Les produits en rupture ou sous seuil apparaissent dans les alertes.',
    ],
    callouts: [
      {
        type: 'warning',
        text: 'Ne corrigez pas le stock au hasard. Cherchez d’abord la cause: vente oubliée, achat non validé, retour ou erreur de comptage.',
      },
    ],
    figures: [
      {
        src: screenshot('stock-movements'),
        caption: 'Figure 11 : Mouvements de stock.',
        description: 'Chaque mouvement explique le changement de quantité.',
        annotations: [
          { label: '1', x: 25, y: 28, text: 'Filtres par produit ou type.' },
          { label: '2', x: 48, y: 57, text: 'Stock avant et stock après.' },
          { label: '3', x: 72, y: 57, text: 'Motif du mouvement.' },
        ],
      },
    ],
  },
  {
    title: '12. Réaliser une vente en caisse',
    module: 'Ventes',
    path: 'Layout caissier > Caisse · /backoffice/cashier/ventes',
    routeName: 'CashierSales',
    summary: 'La caisse permet de rechercher les produits, construire le panier, choisir le client, encaisser et imprimer le ticket.',
    example: 'Exemple: un client achète deux pains et une bouteille d’eau. Ajoutez les produits, vérifiez le total, encaissez et imprimez le ticket.',
    advice: 'Relisez le panier avec le client avant de finaliser la vente.',
    objective: 'Vendre rapidement sans perdre le contrôle du stock.',
    scenario: 'Un client achète deux pains et une bouteille d’eau.',
    steps: [
      'Ouvrez la caisse et vérifiez que la session est ouverte.',
      'Recherchez le produit par nom ou scannez son code-barres.',
      'Cliquez sur le produit pour l’ajouter au panier.',
      'Ajustez la quantité si nécessaire.',
      'Sélectionnez le client si la vente doit être suivie ou faite à crédit.',
      'Cliquez sur encaisser, choisissez le mode de paiement et validez.',
      'Imprimez le ticket si nécessaire.',
    ],
    impacts: [
      'Le stock baisse après validation de la vente.',
      'Le montant entre dans le résumé de la journée du caissier.',
      'Le panier en cours est sauvegardé localement jusqu’à l’enregistrement backend.',
    ],
    commonErrors: [
      'Produit en rupture: il ne peut pas être ajouté sauf si la vente avec stock négatif est autorisée.',
      'Montant reçu inférieur au total: le paiement espèces ne peut pas être validé.',
      'Mauvais client sélectionné: vérifiez avant une vente à crédit.',
    ],
    figures: [
      {
        src: screenshot('cashier-sale'),
        caption: 'Figure 12 : Vente rapide en caisse.',
        description: 'Les derniers produits ajoutés apparaissent en haut du panier.',
        annotations: [
          { label: '1', x: 25, y: 25, text: 'Recherche produit par nom, SKU ou code-barres.' },
          { label: '2', x: 38, y: 55, text: 'Catalogue produit.' },
          { label: '3', x: 78, y: 42, text: 'Panier client.' },
          { label: '4', x: 78, y: 78, text: 'Bouton d’encaissement.' },
        ],
      },
      {
        src: screenshot('sale-payment'),
        caption: 'Figure 13 : Encaissement et impression ticket.',
        description: 'Le mode de paiement détermine l’impact sur la caisse et les rapports.',
        annotations: [
          { label: '1', x: 35, y: 43, text: 'Choix du mode de paiement.' },
          { label: '2', x: 59, y: 55, text: 'Montant reçu.' },
          { label: '3', x: 70, y: 75, text: 'Validation et ticket.' },
        ],
      },
    ],
  },
  {
    title: '13. Gérer la fin de journée et le billetage',
    module: 'Caisse',
    path: 'Layout caissier > Fin journée · /backoffice/cashier/fin-journee',
    routeName: 'CashierEndOfDay',
    summary: 'Le récap de fin de journée affiche les ventes, les flux d’espèces, les produits vendus, les transactions portefeuille, la clôture réelle et l’action de billetage.',
    example: 'Exemple: le caissier vérifie les recharges et dépôts portefeuille, compte les espèces, explique un éventuel écart puis clôture.',
    advice: 'On ne clôture pas une journée future; choisissez la bonne date et traitez les ventes en attente avant de confirmer.',
    objective: 'Fermer proprement la caisse et garder une trace de la journée.',
    scenario: 'Le caissier termine sa journée et souhaite clôturer sa caisse.',
    steps: [
      'Ouvrez Fin de journée.',
      'Vérifiez le fond initial, les espèces encaissées, les recharges, les dépôts, les retraits et le montant théorique.',
      'Consultez les produits vendus et les transactions portefeuille.',
      'Cliquez sur “Clôturer ma journée”.',
      'Si le billetage est actif, saisissez les quantités de billets et pièces.',
      'Les pièces de 10 F et 5 F sont disponibles dans le détail du comptage.',
      'Si le billetage est désactivé, ajoutez seulement un commentaire si nécessaire.',
      'Confirmez la clôture.',
    ],
    impacts: [
      'Les ventes en attente peuvent être abandonnées à la clôture après confirmation.',
      'Le billetage compare le montant théorique et le montant compté.',
      'La clôture simple enregistre la journée sans détail de billets.',
      'Après enregistrement, seules les lignes dont la quantité est supérieure à zéro sont affichées.',
      'Le badge placé près de l’aide indique si la journée est ouverte, en contrôle ou clôturée.',
      'La carte et le formulaire de clôture restent disponibles lorsqu’une session ou un billetage existe, même sans vente.',
      'Clôturer une date passée conserve la session de caisse ouverte aujourd’hui.',
    ],
    figures: [
      {
        src: screenshot('end-of-day'),
        caption: 'Figure 14 : Récap de fin de journée.',
        description: 'Les cartes et tableaux rapprochent les espèces, les ventes et les opérations portefeuille avant la clôture.',
        annotations: [
          { label: '1', x: 23, y: 27, text: 'Totaux détaillés des espèces.' },
          { label: '2', x: 50, y: 55, text: 'Produits vendus.' },
          { label: '3', x: 50, y: 76, text: 'Transactions portefeuille.' },
          { label: '4', x: 83, y: 28, text: 'Clôture de la journée.' },
        ],
      },
    ],
  },
  {
    title: '14. Créer et valider un inventaire',
    module: 'Inventaires',
    path: 'Backoffice > Stock > Inventaires · /backoffice/inventaires',
    routeName: 'Inventories',
    summary: 'Créez un inventaire, comptez les produits, clôturez le comptage puis validez pour corriger automatiquement le stock.',
    example: 'Exemple: à la fin du mois, comptez le rayon Riz et validez les écarts après vérification.',
    advice: 'Clôturer bloque le comptage; valider applique les écarts au stock.',
    objective: 'Comparer le stock logiciel au stock réel en boutique.',
    scenario: 'Je souhaite vérifier le stock réel à la fin du mois.',
    steps: [
      'Créez un inventaire sur tous les produits ou sur un rayon.',
      'Ouvrez l’écran de comptage.',
      'Recherchez le produit par nom, SKU ou code-barres.',
      'Saisissez la quantité réellement comptée.',
      'Validez chaque ligne comptée.',
      'Clôturez le comptage puis validez l’inventaire.',
    ],
    impacts: [
      'Les produits non comptés gardent leur stock tant que l’inventaire n’est pas validé selon les règles prévues.',
      'Les écarts validés créent des mouvements de stock.',
      'La recherche locale facilite le comptage sans modifier l’inventaire.',
    ],
    figures: [
      {
        src: screenshot('inventory-count'),
        caption: 'Figure 15 : Comptage inventaire.',
        description: 'La recherche aide à retrouver rapidement le produit à compter.',
        annotations: [
          { label: '1', x: 27, y: 26, text: 'Recherche locale.' },
          { label: '2', x: 45, y: 54, text: 'Carte produit à compter.' },
          { label: '3', x: 74, y: 57, text: 'Quantité réelle.' },
        ],
      },
    ],
  },
  {
    title: '15. Exploiter les rapports et statistiques',
    module: 'Rapports',
    path: 'Backoffice > Rapports · /backoffice/rapports/*',
    routeName: 'SalesReport',
    summary: 'Analysez les ventes, marges, achats, produits, stocks, inventaires, portefeuilles, caisses, livraisons et rapports comptables.',
    example: 'Exemple: ouvrez Chiffre d’affaires & marges pour comparer le revenu, le coût estimé et la contribution de chaque produit.',
    advice: 'Comparez toujours une période complète pour éviter les conclusions trop rapides.',
    objective: 'Prendre de meilleures décisions à partir des chiffres.',
    scenario: 'Je souhaite connaître mon chiffre d’affaires du mois.',
    steps: [
      'Ouvrez Rapports.',
      'Choisissez le rapport adapté: ventes, chiffre d’affaires et marges, achats, produits, stock, portefeuille, caisse ou comptabilité.',
      'Sélectionnez la période.',
      'Utilisez les filtres si vous voulez un site, un caissier ou une catégorie.',
      'Cliquez sur “Colonnes” pour conserver uniquement les informations utiles.',
      'Lisez les totaux et les tableaux de détail.',
      'Exportez vers Excel; seules les colonnes visibles sont incluses.',
    ],
    impacts: [
      'Les ventes validées alimentent le chiffre d’affaires.',
      'Les achats et prix d’achat aident à calculer les marges.',
      'Les ruptures et seuils minimum aident à préparer les commandes.',
    ],
    figures: [
      {
        src: screenshot('reports'),
        caption: 'Figure 16 : Rapport de ventes.',
        description: 'Les filtres de période permettent d’analyser les chiffres du mois.',
        annotations: [
          { label: '1', x: 25, y: 25, text: 'Choix de période.' },
          { label: '2', x: 48, y: 45, text: 'Cartes de synthèse.' },
          { label: '3', x: 50, y: 72, text: 'Tableau détaillé.' },
        ],
      },
    ],
  },
  {
    title: 'Consulter les journées de caisse',
    module: 'Ventes',
    path: 'Backoffice > Ventes > Journées · /backoffice/ventes/journees',
    routeName: 'SalesDaySummary',
    summary: 'Consultez le résumé de journée du site, filtrez par date ou caissier, puis analysez les ventes, paiements, sessions, produits vendus et transactions portefeuille.',
    example: 'Exemple: choisissez la date d’hier et un caissier pour vérifier ses produits vendus, ses opérations portefeuille et son écart de billetage.',
    advice: 'Utilisez cette page en lecture seule pour contrôler une journée sans clôturer ni modifier la caisse.',
    objective: 'Contrôler les journées de tous les caissiers depuis l’administration.',
    scenario: 'Le gérant veut vérifier la journée d’un caissier sans ouvrir la caisse à sa place.',
    steps: [
      'Ouvrez Ventes puis Journées.',
      'Choisissez la date.',
      'Filtrez sur un caissier si nécessaire.',
      'Lisez les totaux, les sessions, les produits vendus et les transactions portefeuille.',
      'Autorisez une reprise seulement si une session fermée doit être complétée.',
    ],
    impacts: [
      'Cette page ne clôture pas une caisse.',
      'Elle aide à contrôler les produits vendus et les flux électroniques par journée.',
      'Elle permet au gérant d’autoriser une reprise de caisse fermée quand c’est justifié.',
    ],
    figures: [
      {
        src: screenshot('admin-day-summary'),
        caption: 'Figure 17 : Journées de caisse administrateur.',
        description: 'Le gérant filtre par date et par caissier pour contrôler la journée.',
        annotations: [
          { label: '1', x: 26, y: 27, text: 'Filtres date et caissier.' },
          { label: '2', x: 49, y: 47, text: 'Totaux de la journée.' },
          { label: '3', x: 72, y: 72, text: 'Sessions et transactions portefeuille.' },
        ],
      },
    ],
  },
  {
    title: 'Autoriser la reprise d’une caisse fermée',
    module: 'Ventes',
    path: 'Backoffice > Ventes > Journées ou Backoffice > Caisses > Détail caisse',
    routeName: 'SalesDaySummary',
    summary: 'Accordez temporairement à une caissière l’accès à une session fermée sans billetage validé pour compléter des ventes ou brouillons.',
    example: 'Exemple: autorisez une reprise pendant 2 heures avec le motif “coupure réseau”, puis révoquez l’accès après correction.',
    advice: 'N’autorisez jamais une reprise sur une journée définitivement validée; documentez toujours le motif.',
    objective: 'Permettre une correction contrôlée après un dérangement réseau ou une interruption.',
    scenario: 'La ligne internet a coupé et la caissière doit compléter des ventes d’une journée passée.',
    steps: [
      'Ouvrez Ventes > Journées ou le détail de la caisse.',
      'Trouvez la session fermée concernée.',
      'Cliquez sur “Autoriser la reprise”.',
      'Saisissez un motif clair et une durée.',
      'Confirmez.',
      'Révoquez l’accès quand la correction est terminée.',
    ],
    impacts: [
      'La session reste fermée dans l’historique.',
      'Les ventes ajoutées restent rattachées à la journée d’origine.',
      'Si l’autorisation expire ou est révoquée, la caissière est automatiquement sortie de la session.',
    ],
  },
  {
    title: 'Contrôler les billetages',
    module: 'Billetage',
    path: 'Backoffice > Ventes > Billetage · /backoffice/cash-counts',
    routeName: 'CashCounts',
    summary: 'Consultez les fins de journée créées par les caissiers, corrigez si nécessaire et validez.',
    example: 'Exemple: ouvrez un billetage pour comparer le montant théorique et le montant compté.',
    advice: 'Demandez un commentaire quand il existe un écart de caisse.',
    objective: 'Vérifier les espèces et les écarts de caisse.',
    scenario: 'Le montant compté ne correspond pas au montant théorique.',
    steps: [
      'Ouvrez Billetage.',
      'Filtrez la date ou le caissier.',
      'Ouvrez le détail.',
      'Comparez le montant théorique, le montant compté et l’écart.',
      'Validez quand le contrôle est terminé.',
    ],
    impacts: [
      'Un billetage validé verrouille la journée concernée.',
      'Les écarts servent au contrôle interne.',
    ],
  },
  {
    title: 'Configurer les comptes comptables',
    module: 'Comptabilité',
    path: 'Backoffice > Paramètres > Comptabilité · /backoffice/parametres/comptabilite',
    routeName: 'SettingsAccounting',
    summary: 'Associez chaque rôle comptable du site à un compte imputable: caisse, banque, clients, fournisseurs, taxes, stock, remises et écarts.',
    example: 'Exemple: choisissez le compte de TVA collectée, puis enregistrez pour faire disparaître ce compte des éléments manquants.',
    advice: 'Ne lancez pas les écritures automatiques tant que la configuration n’est pas complète.',
    objective: 'Préparer les rapports comptables et éviter les anomalies.',
    scenario: 'Le gérant veut que les ventes et achats soient classés dans les bons comptes.',
    steps: [
      'Ouvrez Paramètres puis Comptabilité.',
      'Associez chaque rôle comptable à un compte.',
      'Enregistrez.',
      'Contrôlez ensuite les anomalies comptables.',
    ],
  },
  {
    title: 'Contrôler les rapports comptables',
    module: 'Comptabilité',
    path: 'Backoffice > Rapports > Journal comptable / Grand livre / Balance / Anomalies',
    routeName: 'AccountingJournalReport',
    summary: 'Consultez les écritures comptables, les lignes par compte, les soldes de balance et les anomalies à corriger.',
    example: 'Exemple: ouvrez la Balance comptable pour vérifier que les totaux débit et crédit restent cohérents sur la période.',
    advice: 'Traitez les anomalies comptables avant d’utiliser les rapports pour une clôture ou un audit.',
    objective: 'Contrôler la cohérence comptable des opérations.',
    scenario: 'Avant une réunion mensuelle, le gérant veut vérifier que les écritures comptables sont équilibrées.',
    steps: [
      'Ouvrez les rapports comptables.',
      'Choisissez la période.',
      'Commencez par les anomalies.',
      'Consultez ensuite le journal, le grand livre et la balance.',
    ],
  },
  {
    title: 'Créer et sécuriser les utilisateurs',
    module: 'Utilisateurs',
    path: 'Backoffice > Utilisateurs > Comptes utilisateurs · /backoffice/utilisateurs',
    routeName: 'Users',
    summary: 'Créez les comptes, choisissez le rôle, rattachez le site et réinitialisez les mots de passe quand c’est nécessaire.',
    example: 'Exemple: créez un caissier pour un nouveau site et remettez-lui le mot de passe temporaire généré.',
    advice: 'Attribuez uniquement les droits nécessaires à la mission de l’utilisateur.',
    objective: 'Contrôler qui peut accéder aux différentes parties du logiciel.',
    scenario: 'Un nouveau caissier rejoint la boutique et doit vendre uniquement sur son site.',
    steps: [
      'Ouvrez Utilisateurs.',
      'Créez un compte avec le rôle adapté.',
      'Rattachez le bon site.',
      'Communiquez le mot de passe temporaire à la personne concernée.',
      'Demandez-lui de changer le mot de passe à la première connexion.',
    ],
    impacts: [
      'Un utilisateur lié à des ventes ne doit pas être supprimé; désactivez-le pour conserver l’historique.',
      'Le nom d’utilisateur sert d’identifiant stable et ne doit pas être modifié après création.',
    ],
  },
  {
    title: 'Lire les activités récentes',
    module: 'Activités récentes',
    path: 'Backoffice > Utilisateurs > Activités récentes · /backoffice/utilisateurs/activites-recentes',
    routeName: 'ActivityLogs',
    summary: 'Consultez les créations, modifications et suppressions réalisées dans l’application. Les activités par module sont réservées au superadmin et au support.',
    example: 'Exemple: filtrez les activités par méthode ou période; le filtre “Module” et les raccourcis d’activités par module ne s’affichent que pour le superadmin et le support.',
    advice: 'Utilisez ce journal pour comprendre les changements avant de corriger une donnée, et demandez au superadmin ou au support si un audit par module est nécessaire.',
    objective: 'Comprendre qui a fait quoi dans l’application.',
    scenario: 'Le stock d’un produit a changé et le gérant veut savoir quelle action a été faite.',
    steps: [
      'Ouvrez Activités récentes.',
      'Filtrez par période, méthode ou utilisateur.',
      'Lisez le module, l’action et la date.',
      'Ouvrez ensuite la donnée concernée si une correction est nécessaire.',
    ],
  },
  {
    title: 'Piloter les nouveaux indicateurs du tableau de bord',
    module: 'Tableau de bord',
    path: 'Backoffice > Tableau de bord · /backoffice/tableau-de-bord',
    routeName: 'Dashboard',
    summary: 'Les onglets Portefeuille, Réapprovisionnement et Expirations complètent les indicateurs de ventes, stock et opérations.',
    example: 'Exemple: sélectionnez 30 jours, contrôlez les recharges dans Portefeuille, puis ouvrez Réapprovisionnement pour préparer les prochaines commandes.',
    advice: 'La période agit sur les activités historiques; les soldes portefeuille et le réapprovisionnement représentent l’état actuel.',
    objective: 'Repérer rapidement les anomalies et les actions prioritaires.',
    steps: [
      'Choisissez Aujourd’hui, 7 jours ou 30 jours.',
      'Survolez les cartes KPI pour lire leur évolution par rapport à la période précédente.',
      'Ouvrez Portefeuille pour comparer recharges, dépôts, retraits et soldes.',
      'Ouvrez Réapprovisionnement pour traiter les ruptures et seuils minimum.',
      'Ouvrez Expirations pour retrouver les lots arrivés à échéance sur la période.',
    ],
    impacts: [
      'Les flèches montrent le sens mathématique de l’évolution, sans qualifier automatiquement la hausse de bonne ou mauvaise.',
      'Les données restent limitées au site connecté.',
    ],
    figures: [{
      src: screenshot('dashboard-operations'),
      caption: 'Nouveauté : onglets opérationnels du tableau de bord.',
      description: 'Les onglets Portefeuille, Réapprovisionnement et Expirations regroupent les décisions rapides.',
      annotations: [
        { label: '1', x: 34, y: 20, text: 'Période actuellement analysée.' },
        { label: '2', x: 48, y: 37, text: 'Nouveaux onglets opérationnels.' },
        { label: '3', x: 25, y: 61, text: 'Cartes KPI et évolution.' },
        { label: '4', x: 70, y: 76, text: 'Tableau ou graphique de détail.' },
      ],
    }],
  },
  {
    title: 'Utiliser les outils globaux de Mes sites',
    module: 'Multisite',
    path: 'Backoffice > Mes sites · /backoffice/mes-sites',
    routeName: 'Sites',
    summary: 'Mes sites permet de changer de boutique et d’ouvrir la base produits ou les utilisateurs de tous les sites selon les permissions accordées.',
    example: 'Exemple: activez un produit global dans toutes les boutiques actives, puis vérifiez les comptes depuis la liste globale des utilisateurs.',
    advice: 'Après avoir changé de boutique, vérifiez toujours le nom du site courant avant une opération métier.',
    objective: 'Administrer plusieurs boutiques sans mélanger leurs données opérationnelles.',
    steps: [
      'Ouvrez Mes sites depuis la topbar.',
      'Cliquez sur la partie supérieure d’une carte pour consulter le site ou utilisez Se connecter.',
      'Ouvrez Base de donnée si vous possédez la permission Produits.',
      'Choisissez une activation sur le site connecté ou sur tous les sites actifs autorisés.',
      'Ouvrez Utilisateurs pour consulter les comptes de tous les sites.',
    ],
    impacts: [
      'Les permissions shops.view et shops.switch contrôlent séparément la consultation et le changement de boutique.',
      'Les actions de gestion restent masquées aux utilisateurs qui peuvent seulement consulter et se connecter.',
    ],
    figures: [{
      src: screenshot('all-sites-tools'),
      caption: 'Nouveauté : outils globaux depuis Mes sites.',
      description: 'La liste des boutiques centralise le changement de site, la base produits et les utilisateurs globaux.',
      annotations: [
        { label: '1', x: 24, y: 20, text: 'Base de donnée globale.' },
        { label: '2', x: 48, y: 20, text: 'Utilisateurs tous sites.' },
        { label: '3', x: 28, y: 57, text: 'Boutique actuellement connectée.' },
        { label: '4', x: 74, y: 72, text: 'Action Se connecter selon permission.' },
      ],
    }],
  },
  {
    title: 'Consulter le calendrier des opérations de stock',
    module: 'Stock',
    path: 'Backoffice > Stock > Calendrier · /backoffice/calendrier-stock',
    routeName: 'StockCalendar',
    summary: 'Le calendrier hebdomadaire rassemble ventes validées, achats validés, mouvements et expirations de lots.',
    example: 'Exemple: cliquez sur une expiration rouge pour retrouver le produit, le lot, la date et les achats associés.',
    advice: 'La semaine visible détermine les données chargées; utilisez la légende avant d’interpréter les couleurs.',
    objective: 'Comprendre chronologiquement l’activité du stock.',
    steps: [
      'Ouvrez Stock puis Calendrier.',
      'Naviguez avec Précédent, Aujourd’hui et Suivant.',
      'Identifiez ventes, achats, mouvements et expirations grâce aux couleurs.',
      'Cliquez sur un événement pour ouvrir son dialogue de détail.',
      'Suivez le lien vers la vente, l’achat ou le produit lorsqu’il est proposé.',
    ],
    figures: [{
      src: screenshot('stock-calendar'),
      caption: 'Nouveauté : calendrier hebdomadaire du stock.',
      description: 'Les opérations et expirations sont positionnées dans la semaine visible.',
      annotations: [
        { label: '1', x: 24, y: 20, text: 'Navigation entre les semaines.' },
        { label: '2', x: 68, y: 20, text: 'Légende des couleurs.' },
        { label: '3', x: 45, y: 57, text: 'Événements de la semaine.' },
        { label: '4', x: 78, y: 72, text: 'Clic pour ouvrir le détail.' },
      ],
    }],
  },
  {
    title: 'Gérer les produits et portefeuilles électroniques',
    module: 'Portefeuilles',
    path: 'Backoffice > Stock > Portefeuilles électroniques · /backoffice/portefeuilles-electroniques',
    routeName: 'ElectronicWallets',
    summary: 'Un produit électronique utilise un montant libre, un portefeuille actif et une opération recharge, dépôt ou retrait, sans stock physique ni prix fixe.',
    example: 'Exemple: associez Recharge Airtel au portefeuille Airtel Money avec l’opération Recharge, puis consultez chaque écriture dans l’historique.',
    advice: 'N’ajustez jamais un solde sans motif et n’associez pas un produit à un portefeuille d’une autre boutique.',
    objective: 'Configurer et contrôler les services électroniques avec une traçabilité financière complète.',
    steps: [
      'Créez le portefeuille et son éventuel solde initial.',
      'Renseignez le taux de bénéfice par transaction si vous le connaissez.',
      'Créez ou modifiez un produit, choisissez le type Électronique, le portefeuille et l’opération.',
      'Laissez stock, seuil, formule par lot et prix fixe hors du parcours électronique.',
      'Dans la caisse, ouvrez “Autres détails” pour conserver si nécessaire le numéro du client et la référence fournie par l’opérateur.',
      'Filtrez l’historique par portefeuille, opération, statut ou période.',
      'Utilisez un ajustement motivé ou une annulation pour corriger une écriture.',
    ],
    impacts: [
      'Les recharges, dépôts et retraits créent uniquement des transactions portefeuille.',
      'Le taux connu est figé sur la transaction afin que les changements futurs ne modifient pas sa marge.',
      'L’annulation crée un contre-mouvement sans modifier l’écriture originale.',
    ],
    figures: [{
      src: screenshot('electronic-wallets'),
      caption: 'Nouveauté : gestion des portefeuilles électroniques.',
      description: 'Les cartes donnent les soldes et le tableau conserve toutes les écritures.',
      annotations: [
        { label: '1', x: 25, y: 22, text: 'Filtres des opérations.' },
        { label: '2', x: 27, y: 46, text: 'Solde actuel du portefeuille.' },
        { label: '3', x: 55, y: 68, text: 'Historique financier immuable.' },
        { label: '4', x: 83, y: 68, text: 'Contre-mouvement autorisé.' },
      ],
    }],
  },
  {
    title: 'Analyser le chiffre d’affaires, les marges et les portefeuilles',
    module: 'Rapports',
    path: 'Backoffice > Rapports > Chiffre d’affaires & marges ou Portefeuilles',
    routeName: 'RevenueMarginsReport',
    summary: 'Les nouveaux rapports rapprochent chiffre d’affaires, coût estimé, marge par produit et activité financière des portefeuilles.',
    example: 'Exemple: masquez les colonnes secondaires, comparez la marge de chaque produit au chiffre d’affaires total, puis exportez le tableau visible.',
    advice: 'Vérifiez la période et les filtres avant de comparer les cartes de synthèse ou de lancer Excel.',
    objective: 'Mesurer la rentabilité et contrôler les flux électroniques.',
    steps: [
      'Sélectionnez les dates de début et de fin.',
      'Lisez chiffre d’affaires, coût estimé, marge brute et taux de marge.',
      'Dans le rapport portefeuille, filtrez le portefeuille, le type et le statut.',
      'Cliquez sur Colonnes et conservez au moins une colonne de données.',
      'Appliquez puis exportez vers Excel; l’ordre et la sélection visibles sont respectés.',
    ],
    figures: [{
      src: screenshot('revenue-margins'),
      caption: 'Nouveauté : rapport chiffre d’affaires et marges.',
      description: 'La synthèse et le tableau expliquent la rentabilité produit sur la période.',
      annotations: [
        { label: '1', x: 25, y: 20, text: 'Dates analysées.' },
        { label: '2', x: 48, y: 42, text: 'Cartes de synthèse.' },
        { label: '3', x: 72, y: 62, text: 'Marge et part du chiffre d’affaires.' },
        { label: '4', x: 83, y: 20, text: 'Colonnes et export Excel.' },
      ],
    }],
  },
  {
    title: 'Gérer les dépenses et leurs paiements',
    module: 'Dépenses',
    path: 'Backoffice > Dépenses · /backoffice/depenses',
    routeName: 'Expenses',
    summary: 'Le module Dépenses suit les charges ponctuelles ou récurrentes, les montants payés, les soldes et les pièces justificatives.',
    example: 'Exemple: enregistrez une facture d’électricité de 75 000 FCFA, validez-la, puis saisissez un premier paiement de 50 000 FCFA et le solde plus tard.',
    advice: 'Vérifiez le type et les justificatifs avant validation: une dépense validée ne se modifie plus et son historique comptable est conservé.',
    objective: 'Enregistrer et contrôler toutes les charges d’exploitation sans perdre leur historique.',
    scenario: 'Je reçois une facture à payer en plusieurs fois et je veux suivre son reste dû.',
    steps: [
      'Ouvrez Dépenses depuis la barre de navigation.',
      'Cliquez sur “Nouvelle dépense” et renseignez le type, le libellé, le montant et la date.',
      'Ajoutez les justificatifs PDF, JPG ou PNG utiles, dans la limite indiquée.',
      'Pour corriger un brouillon, cliquez sur Modifier dans la liste ou sa fiche : le formulaire s’ouvre dans un dialogue.',
      'Ouvrez la fiche puis validez la dépense après contrôle.',
      'Enregistrez chaque paiement avec sa date et son moyen réel.',
      'En cas d’erreur, utilisez Annuler: l’application conserve l’original et crée les contre-écritures.',
    ],
    impacts: [
      'Un brouillon reste modifiable.',
      'La validation place une dépense non réglée en attente de paiement.',
      'Chaque paiement met à jour le payé et le reste dû, puis crée son écriture comptable.',
      'Une annulation ne supprime jamais l’historique des paiements.',
    ],
    commonErrors: [
      'Type sans compte comptable: configurez-le dans Paramètres > Types de dépenses ou dans les paramètres comptables.',
      'Paiement supérieur au reste dû: réduisez le montant au solde affiché.',
      'Pièce refusée: utilisez un fichier PDF, JPG ou PNG de 10 Mo maximum.',
    ],
    callouts: [
      { type: 'warning', title: 'Attention', text: 'Une dépense d’achat de stock ne doit pas être saisie une seconde fois ici si elle existe déjà dans le module Achats.' },
      { type: 'know', title: 'Paiements partiels', text: 'Les paiements sont append-only: les lignes restent visibles même lorsqu’une annulation génère un contre-paiement.' },
    ],
    subsections: [
      { title: 'Synthèse des dépenses', text: 'Si votre compte peut consulter le dashboard, ouvrez son onglet “Dépenses d’exploitation” pour retrouver les indicateurs, graphiques et dépenses récentes de la période.' },
      { title: 'Types de dépenses', text: 'Ouvrez Paramètres > Types de dépenses. Les types système gardent leur identité, mais leur description, compte comptable, ordre et activation peuvent être adaptés au site.' },
      { title: 'Planifications', text: 'Dans une nouvelle dépense ou un brouillon, cochez « Planifier la récurrence de cette dépense ». Les répétitions partent de son échéance, ou de sa date si aucune échéance n’est renseignée. Elles sont préparées dès l’enregistrement, sans paiement automatique. Le bouton Planifications de la page Dépenses permet de modifier, mettre en pause ou reprendre les règles ; la dépense d’origine est conservée.' },
      { title: 'Rapport des dépenses', text: 'Filtrez par période, type, statut ou moyen de paiement. Excel exporte les lignes affichées ; « Exporter toutes les pages » récupère toutes les dépenses correspondant aux filtres.' },
    ],
    keywords: ['dépense', 'charge', 'paiement', 'facture', 'justificatif', 'récurrence', 'reste dû'],
  },
  {
    title: 'Comprendre les nouveautés de la version',
    module: 'Aide',
    path: 'Démarrage authentifié > Nouveautés Suite241',
    summary: 'À chaque nouvelle version documentée, les gérants et superadmins reçoivent un dialogue présentant fonctionnalités, améliorations et correctifs.',
    example: 'Exemple: lisez les notes 1.3.0, cochez la confirmation puis cliquez sur J’ai compris pour ne plus revoir le dialogue avant la prochaine version.',
    advice: 'Fermer le dialogue sans confirmer reporte l’accusé de lecture au prochain démarrage authentifié.',
    objective: 'Informer les responsables des changements avant leur utilisation en production.',
    steps: [
      'Lisez les trois sections du dialogue.',
      'Vérifiez les points importants destinés aux utilisateurs.',
      'Cochez “J’ai lu et compris les nouveautés de cette version”.',
      'Cliquez sur “J’ai compris”.',
    ],
  },
  {
    title: 'Questions fréquentes',
    module: 'Aide',
    path: 'Manuel d’utilisation > Questions fréquentes',
    summary: 'Cette section répond aux problèmes courants rencontrés en boutique.',
    example: 'Exemple: un produit a été vendu par erreur, le stock est négatif ou l’imprimante ne réagit plus.',
    advice: 'Corrigez toujours depuis l’action prévue par le logiciel, pas directement dans la base de données.',
    objective: 'Résoudre les blocages les plus fréquents sans assistance technique.',
    scenario: 'Un utilisateur rencontre une erreur pendant une journée de vente.',
    subsections: [
      {
        title: 'J’ai vendu un produit par erreur',
        text: 'Ouvrez la vente, utilisez l’action d’annulation ou de retour selon le cas, puis vérifiez le stock.',
      },
      {
        title: 'Mon stock est négatif',
        text: 'Vérifiez si le produit autorise la vente avec stock négatif. Contrôlez ensuite les achats non validés et les mouvements de stock.',
      },
      {
        title: 'J’ai oublié mon mot de passe',
        text: 'Demandez au gérant ou à l’administrateur de réinitialiser votre compte. Vous devrez choisir un nouveau mot de passe.',
      },
      {
        title: 'Je ne retrouve pas une vente',
        text: 'Filtrez l’historique par date, caissier, client ou mode de paiement. Vérifiez aussi les ventes abandonnées si la vente n’a pas été finalisée.',
      },
      {
        title: 'Une imprimante ne fonctionne plus',
        text: 'Vérifiez le papier, la connexion, les paramètres d’impression et le mode navigateur. Relancez le poste si nécessaire.',
      },
    ],
  },
  {
    title: 'Bonnes pratiques quotidiennes',
    module: 'Aide',
    path: 'Manuel d’utilisation > Bonnes pratiques',
    summary: 'Quelques habitudes simples permettent de garder les chiffres fiables et la caisse propre.',
    example: 'Exemple: contrôler les ruptures chaque soir évite de manquer de produits très vendus le lendemain.',
    advice: 'La régularité vaut mieux qu’une grande correction en fin de mois.',
    objective: 'Utiliser Suite241 de façon fiable dans la durée.',
    scenario: 'Le gérant veut éviter les écarts de stock et les erreurs de caisse.',
    steps: [
      'Réalisez un inventaire régulièrement, surtout sur les produits sensibles.',
      'Validez les achats seulement après réception réelle.',
      'Contrôlez les mouvements de stock avant de corriger.',
      'Clôturez la caisse à la fin de chaque journée.',
      'Utilisez des mots de passe personnels et sécurisés.',
      'Désactivez les utilisateurs ou produits historiques au lieu de les supprimer.',
    ],
    impacts: [
      'Les rapports deviennent plus fiables.',
      'Les ruptures sont détectées plus tôt.',
      'Les erreurs sont plus faciles à expliquer.',
    ],
  },
];

export const cashierManualItems: ManualItem[] = [
  {
    title: 'Ouvrir l’interface de caisse',
    module: 'Caisse',
    path: 'Layout caissier > Caisse · /backoffice/cashier/ventes',
    routeName: 'CashierSales',
    summary: 'La caisse permet de rechercher les produits, construire le panier, choisir le client et enregistrer la vente.',
    example: 'Exemple: scannez un code-barres ou tapez le nom du produit pour l’ajouter au panier.',
    advice: 'Vérifiez que le badge indique “En ligne” avant une vente à crédit.',
    objective: 'Commencer une journée de vente avec une session de caisse correcte.',
    scenario: 'La caissière arrive le matin et doit ouvrir sa caisse avant la première vente.',
    steps: [
      'Connectez-vous avec votre compte.',
      'Ouvrez l’espace Caisse.',
      'Choisissez la caisse si le logiciel le demande.',
      'Saisissez le fonds de caisse.',
      'Confirmez l’ouverture.',
    ],
    impacts: [
      'Toutes les ventes seront rattachées à cette session.',
      'Le fonds de caisse sert au calcul du montant théorique.',
    ],
    figures: [
      {
        src: screenshot('cashier-sale'),
        caption: 'Figure 1 : Interface de caisse.',
        description: 'La recherche, le catalogue et le panier sont visibles sur le même écran.',
        annotations: [
          { label: '1', x: 25, y: 25, text: 'Recherche produit.' },
          { label: '2', x: 78, y: 42, text: 'Panier.' },
          { label: '3', x: 78, y: 78, text: 'Encaissement.' },
        ],
      },
    ],
  },
  {
    title: 'Rechercher un produit',
    module: 'Recherche produit',
    path: 'Layout caissier > Caisse > Champ de recherche',
    routeName: 'CashierSales',
    summary: 'Recherchez par nom, SKU ou code-barres pour ajouter rapidement un article, quel que soit son rayon.',
    example: 'Exemple: tapez “savon” pour retrouver le produit même si le rayon actuellement affiché est différent.',
    advice: 'Privilégiez le code-barres pour éviter les erreurs de sélection et vérifiez les alertes de rupture avant ajout.',
    objective: 'Trouver rapidement le bon produit.',
    scenario: 'Un client achète une bouteille d’eau mais le rayon affiché est “Boulangerie”.',
    steps: [
      'Cliquez dans le champ de recherche.',
      'Tapez le nom, le SKU ou scannez le code-barres.',
      'Cliquez sur le bon produit.',
      'Vérifiez que le produit ajouté apparaît en haut du panier.',
    ],
    impacts: [
      'La recherche filtre tous les produits, peu importe le rayon sélectionné.',
      'Un produit en rupture non autorisé ne peut pas être ajouté.',
    ],
  },
  {
    title: 'Gérer le panier',
    module: 'Panier',
    path: 'Layout caissier > Caisse > Panier',
    routeName: 'CashierSales',
    summary: 'Modifiez les quantités, supprimez une ligne ou vérifiez le total avant l’enregistrement.',
    example: 'Exemple: si le client prend 3 unités, augmentez la quantité sur la ligne du produit.',
    advice: 'Relisez le panier avec le client avant de finaliser la vente.',
    objective: 'Éviter les erreurs avant encaissement.',
    scenario: 'Le client ajoute un produit au dernier moment et le panier est déjà long.',
    steps: [
      'Ajoutez le produit depuis le catalogue ou le scan.',
      'Le dernier produit ajouté remonte en haut du panier.',
      'Contrôlez la quantité et le prix.',
      'Supprimez la ligne si elle a été ajoutée par erreur.',
    ],
    impacts: [
      'Le panier en cours est sauvegardé localement pour éviter de le perdre après un refresh.',
      'La sauvegarde locale est supprimée après l’enregistrement en backend.',
    ],
    callouts: [
      {
        type: 'know',
        text: 'La sauvegarde locale protège uniquement le panier en cours. Une vente sauvegardée dans le serveur reste la référence.',
      },
    ],
  },
  {
    title: 'Vendre avec ou sans stock disponible',
    module: 'Stock caisse',
    path: 'Layout caissier > Caisse > Catalogue produit',
    routeName: 'CashierSales',
    summary: 'Un produit à stock zéro ne peut pas être ajouté au panier, sauf si le gérant a autorisé la vente avec stock négatif sur ce produit.',
    example: 'Exemple: le pain peut être vendu toute la journée malgré une entrée de stock pas encore enregistrée si l’option est autorisée.',
    advice: 'Prévenez le gérant si un produit est souvent vendu en stock négatif afin de corriger les habitudes d’approvisionnement.',
    objective: 'Éviter de vendre un produit réellement indisponible.',
    scenario: 'Le stock logiciel d’un beignet est à zéro, mais la production vient d’arriver au comptoir.',
    steps: [
      'Essayez d’ajouter le produit.',
      'Si la carte est désactivée, le produit ne peut pas être vendu.',
      'Si “Stock négatif autorisé” apparaît, vous pouvez vendre.',
      'La vente finale reste contrôlée par le serveur.',
    ],
    impacts: [
      'La vente diminue le stock, qui peut devenir négatif pour les produits autorisés.',
      'Les produits non autorisés restent bloqués.',
    ],
  },
  {
    title: 'Encaisser et imprimer le ticket',
    module: 'Encaissement',
    path: 'Layout caissier > Caisse > Bouton Enregistrer',
    routeName: 'CashierSales',
    summary: 'Choisissez le mode de paiement puis enregistrez la vente. Pour espèces, saisissez le montant reçu.',
    example: 'Exemple: total 8 000 FCFA, montant reçu 10 000 FCFA, montant rendu 2 000 FCFA.',
    advice: 'Ouvrez le tiroir uniquement au moment de finaliser le paiement espèces.',
    objective: 'Finaliser la vente et produire le ticket client.',
    scenario: 'Un client paie deux pains et une bouteille d’eau en espèces.',
    steps: [
      'Cliquez sur encaisser.',
      'Choisissez “Espèces”.',
      'Saisissez le montant reçu.',
      'Vérifiez le montant rendu.',
      'Validez.',
      'Imprimez le ticket si nécessaire.',
    ],
    impacts: [
      'La vente est validée.',
      'Le stock baisse.',
      'Le montant est ajouté à la journée de caisse.',
    ],
    figures: [
      {
        src: screenshot('sale-payment'),
        caption: 'Figure 2 : Dialogue de paiement.',
        description: 'Le mode de paiement et le montant reçu doivent correspondre à la réalité.',
        annotations: [
          { label: '1', x: 35, y: 43, text: 'Mode de paiement.' },
          { label: '2', x: 59, y: 55, text: 'Montant reçu.' },
          { label: '3', x: 70, y: 75, text: 'Validation.' },
        ],
      },
    ],
  },
  {
    title: 'Vendre à crédit',
    module: 'Crédit client',
    path: 'Layout caissier > Caisse > Mode de paiement Crédit',
    routeName: 'CashierSales',
    summary: 'Le crédit valide la vente, déduit le stock et augmente la dette du client sans ajouter d’espèces en caisse.',
    example: 'Exemple: si le crédit disponible est 50 000 FCFA, une vente de 20 000 FCFA est acceptée.',
    advice: 'Refusez le crédit si le client dépasse son plafond ou n’est pas identifié.',
    objective: 'Enregistrer une dette client proprement.',
    scenario: 'Un client fidèle prend des produits et paiera en fin de semaine.',
    steps: [
      'Sélectionnez le client avant paiement.',
      'Vérifiez le plafond et la dette actuelle.',
      'Choisissez le paiement à crédit.',
      'Validez la vente.',
    ],
    impacts: [
      'Le stock baisse immédiatement.',
      'La caisse espèces n’augmente pas.',
      'La dette du client augmente.',
    ],
  },
  {
    title: 'Réaliser une opération électronique',
    module: 'Portefeuille électronique',
    path: 'Layout caissier > Caisse · /backoffice/cashier/ventes',
    routeName: 'CashierSales',
    summary: 'Un service électronique affiche “Montant libre” et “Service électronique”; il ne possède ni prix fixe ni stock physique.',
    example: 'Exemple: sélectionnez Recharge Airtel, saisissez 5 000 FCFA dans le dialogue puis validez directement l’opération.',
    advice: 'Vérifiez le portefeuille, le type d’opération et le montant avec le client avant de confirmer.',
    objective: 'Enregistrer une recharge, un dépôt ou un retrait sans modifier le stock physique.',
    scenario: 'Un client demande une recharge électronique de 5 000 FCFA.',
    steps: [
      'Recherchez ou scannez le produit électronique.',
      'Vérifiez que la carte indique “Service électronique” et non “Portefeuille indisponible”.',
      'Cliquez sur le produit.',
      'Saisissez un montant supérieur à zéro dans le dialogue.',
      'Ouvrez “Autres détails” si vous souhaitez saisir le numéro du client et la référence opérateur.',
      'Contrôlez le résumé puis validez; aucun moyen de paiement classique n’est demandé.',
      'Consultez la référence et le nouveau solde dans l’historique.',
    ],
    impacts: [
      'La quantité reste toujours égale à 1.',
      'Un panier électronique ne peut pas être mélangé avec des produits physiques.',
      'Recharge, dépôt et retrait restent des opérations portefeuille et ne créent aucune vente.',
      'Une recharge ou un retrait peut être refusé si le solde disponible est insuffisant.',
    ],
    commonErrors: [
      'Portefeuille inactif ou absent: l’opération est bloquée.',
      'Montant nul: la validation est impossible.',
      'Panier physique déjà commencé: terminez ou videz-le avant le service électronique.',
    ],
    figures: [{
      src: screenshot('electronic-sale'),
      caption: 'Nouveauté : montant libre pour un service électronique.',
      description: 'Le dialogue remplace le prix fixe et déclenche directement l’opération portefeuille.',
      annotations: [
        { label: '1', x: 26, y: 45, text: 'Produit électronique sélectionnable sans stock.' },
        { label: '2', x: 55, y: 35, text: 'Portefeuille et opération.' },
        { label: '3', x: 55, y: 57, text: 'Montant libre obligatoire.' },
        { label: '4', x: 72, y: 76, text: 'Validation directe.' },
      ],
    }],
  },
  {
    title: 'Reprendre une vente en attente',
    module: 'Ventes en attente',
    path: 'Layout caissier > Caisse > Ventes en attente',
    routeName: 'CashierSales',
    summary: 'Retrouvez les ventes suspendues de la session de caisse ouverte.',
    example: 'Exemple: suspendez un panier pendant que le client cherche un autre article, puis reprenez-le ensuite.',
    advice: 'À la clôture de caisse, les brouillons restants deviennent abandonnés.',
    objective: 'Mettre un panier de côté sans bloquer la caisse.',
    scenario: 'Un client part chercher un article manquant et la file d’attente avance.',
    steps: [
      'Suspendez la vente.',
      'Servez les autres clients.',
      'Ouvrez les ventes en attente.',
      'Reprenez la vente quand le client revient.',
    ],
    impacts: [
      'Une vente suspendue ne compte pas comme vente validée.',
      'Elle peut être abandonnée lors de la clôture de journée.',
    ],
  },
  {
    title: 'Reprendre une journée fermée autorisée',
    module: 'Reprise caisse',
    path: 'Layout caissier > Caisse > Journées autorisées à compléter',
    routeName: 'CashierSales',
    summary: 'Quand un gérant autorise une reprise, choisissez la journée fermée depuis l’écran d’ouverture de caisse pour compléter des ventes ou brouillons.',
    example: 'Exemple: après une coupure réseau, cliquez sur “Reprendre” sur la journée autorisée, puis ajoutez les ventes manquantes.',
    advice: 'Utilisez ce mode uniquement pendant la durée accordée; le bandeau “Mode reprise de journée fermée” confirme que la session reste fermée dans l’historique.',
    objective: 'Terminer une journée passée sans rouvrir officiellement la caisse.',
    scenario: 'Une coupure réseau a empêché la caissière de finir sa journée.',
    steps: [
      'Demandez au gérant d’autoriser la reprise.',
      'Retournez dans l’écran de caisse.',
      'Choisissez la journée autorisée.',
      'Complétez les ventes ou brouillons.',
      'Cliquez sur “Quitter la reprise” quand c’est terminé.',
    ],
    impacts: [
      'Les ventes créées restent rattachées à l’ancienne session.',
      'Si l’autorisation expire, l’application sort de la session.',
    ],
  },
  {
    title: 'Choisir un inventaire à compter',
    module: 'Inventaire caissier',
    path: 'Layout caissier > Inventaires · /backoffice/cashier/inventaires',
    routeName: 'CashierInventories',
    summary: 'Le caissier voit uniquement les inventaires brouillon du site et choisit celui à compter.',
    example: 'Exemple: cliquez sur l’inventaire “Rayon boissons” pour commencer le comptage.',
    advice: 'Ne commencez pas un inventaire si les produits du rayon sont encore en mouvement.',
    objective: 'Participer au comptage physique du stock.',
    scenario: 'Le gérant demande au caissier de compter le rayon boissons.',
    steps: [
      'Ouvrez Inventaires.',
      'Choisissez l’inventaire à compter.',
      'Recherchez un produit par nom, SKU ou code-barres.',
      'Saisissez la quantité réelle.',
      'Validez la ligne.',
    ],
    impacts: [
      'Le comptage ne corrige pas le stock tant que le gérant ne valide pas l’inventaire.',
    ],
    figures: [
      {
        src: screenshot('inventory-count'),
        caption: 'Figure 3 : Comptage inventaire.',
        description: 'La recherche facilite le comptage quand la liste est longue.',
        annotations: [
          { label: '1', x: 27, y: 26, text: 'Recherche.' },
          { label: '2', x: 74, y: 57, text: 'Quantité réelle.' },
        ],
      },
    ],
  },
  {
    title: 'Préparer la fin de journée',
    module: 'Fin de journée',
    path: 'Layout caissier > Fin journée · /backoffice/cashier/fin-journee',
    routeName: 'CashierEndOfDay',
    summary: 'Consultez le statut, le fond initial, les espèces encaissées, les recharges, les dépôts, les retraits, le montant théorique et les transactions détaillées de la session.',
    example: 'Exemple: rapprochez les espèces physiques des ventes, recharges, dépôts et retraits avant de lancer le billetage.',
    advice: 'On ne clôture pas une journée future; choisissez la bonne date et traitez les ventes en attente avant de confirmer.',
    objective: 'Contrôler la caisse avant de quitter le poste.',
    scenario: 'La caissière termine son service et doit clôturer proprement.',
    steps: [
      'Ouvrez Fin journée.',
      'Vérifiez le badge de statut, les totaux et les ventes en attente.',
      'Comparez Fond initial, Espèces encaissées, Recharge, Dépôts, Retraits et Montant théorique.',
      'Lisez les produits vendus.',
      'Lisez les transactions portefeuille et leurs statuts.',
      'Cliquez sur “Clôturer ma journée”.',
    ],
    impacts: [
      'Les ventes en attente peuvent être abandonnées après confirmation.',
      'Le gérant pourra consulter le résumé de la journée.',
    ],
    figures: [
      {
        src: screenshot('end-of-day'),
        caption: 'Figure 4 : Récap de fin de journée.',
        description: 'Les cartes et tableaux rapprochent les ventes, espèces et opérations portefeuille.',
        annotations: [
          { label: '1', x: 23, y: 27, text: 'Totaux.' },
          { label: '2', x: 50, y: 55, text: 'Produits vendus.' },
          { label: '3', x: 83, y: 28, text: 'Clôture.' },
        ],
      },
    ],
  },
  {
    title: 'Faire le billetage ou la clôture simple',
    module: 'Billetage',
    path: 'Layout caissier > Fin journée > Clôturer ma journée',
    routeName: 'CashierEndOfDay',
    summary: 'Renseignez les quantités de billets et pièces, y compris les pièces de 10 F et 5 F, si le site exige le billetage. Si le billetage est désactivé, seul le commentaire reste visible et facultatif.',
    example: 'Exemple: saisissez 2 billets de 10 000, 5 pièces de 500 et 2 pièces de 10; le total se calcule automatiquement.',
    advice: 'Comptez les espèces calmement avant de confirmer, car le billetage devient non modifiable.',
    objective: 'Fermer la journée selon la règle du site.',
    scenario: 'Le site a activé le billetage détaillé, la caissière doit compter les espèces.',
    steps: [
      'Cliquez sur “Clôturer ma journée”.',
      'Saisissez les quantités de billets et pièces si elles sont demandées.',
      'Dans Paramètres > Général, activez le tiroir-caisse uniquement si ce poste utilise Suite241 Print.',
      'Ajoutez un commentaire en cas d’écart.',
      'Confirmez la clôture.',
    ],
    impacts: [
      'Avec billetage actif, le montant compté est comparé au montant théorique.',
      'Avec clôture simple, l’écart est fixé à zéro et la trace de clôture est enregistrée.',
      'L’avertissement du pont local apparaît seulement si l’ouverture du tiroir est activée et échoue.',
      'La carte “Clôture réelle” reprend la fermeture effective de la session.',
      'Après enregistrement, les dénominations dont la quantité vaut zéro ne sont plus affichées.',
    ],
  },
  {
    title: 'Sortie automatique d’une caisse fermée',
    module: 'Session caisse',
    path: 'Layout caissier > Caisse',
    routeName: 'CashierSales',
    summary: 'Si une action est faite alors que la session est déjà fermée ou que la reprise n’est plus autorisée, l’application sort automatiquement de cette caisse.',
    example: 'Exemple: si le gérant révoque une reprise, la prochaine action ramène la caissière à l’écran d’ouverture sans déconnecter son compte.',
    advice: 'Si ce message apparaît, choisissez une caisse ouverte ou demandez au gérant une nouvelle autorisation de reprise.',
    objective: 'Éviter de continuer à vendre dans une session fermée.',
    scenario: 'La journée est terminée pendant que l’écran de caisse est encore ouvert.',
    steps: [
      'Effectuez une action en caisse.',
      'Si le serveur répond que la session est fermée, l’application sort de la caisse.',
      'Vous restez connecté à votre compte.',
      'Ouvrez une nouvelle caisse ou choisissez une reprise autorisée.',
    ],
    impacts: [
      'Le compte utilisateur reste connecté.',
      'Seule la session de caisse locale est vidée.',
    ],
  },
  {
    title: 'Consulter et saisir une dépense autorisée',
    module: 'Dépenses',
    path: 'Layout caissier > Dépenses · /backoffice/depenses',
    routeName: 'Expenses',
    summary: 'Le raccourci Dépenses apparaît seulement lorsque le compte possède le droit de consulter ce module.',
    example: 'Exemple: un caissier autorisé enregistre un achat urgent de fournitures et joint immédiatement la photo du reçu.',
    advice: 'Choisissez le moyen de paiement réellement utilisé. Une dépense en espèces peut être enregistrée sans être liée à une session de caisse.',
    objective: 'Saisir une charge opérationnelle tout en respectant les droits accordés par le gérant.',
    steps: [
      'Ouvrez le raccourci Dépenses dans la barre caissier.',
      'Consultez la liste et ouvrez la dépense recherchée.',
      'Si votre compte possède le droit de création, cliquez sur “Nouvelle dépense”.',
      'Renseignez les champs et joignez le justificatif.',
      'Laissez la validation, le paiement ou l’annulation au responsable si ces actions ne vous sont pas proposées.',
    ],
    impacts: ['Les boutons visibles dépendent exactement des permissions du compte.', 'La dépense reste rattachée au site courant.'],
  },
  {
    title: 'Questions fréquentes caisse',
    module: 'Aide',
    path: 'Manuel caissier > Questions fréquentes',
    routeName: 'CashierSales',
    summary: 'Retrouvez les réponses aux blocages courants pendant une vente ou une clôture.',
    example: 'Exemple: produit introuvable, stock à zéro, ticket non imprimé ou vente suspendue.',
    advice: 'En cas de doute, suspendez la vente et appelez le gérant avant de valider.',
    objective: 'Réagir calmement aux incidents de caisse.',
    scenario: 'La file d’attente avance et une erreur apparaît.',
    subsections: [
      {
        title: 'Le produit ne s’ajoute pas au panier',
        text: 'Vérifiez le stock. Si le produit est en rupture et non autorisé en stock négatif, il ne peut pas être vendu.',
      },
      {
        title: 'Le ticket ne sort pas',
        text: 'Vérifiez le papier, l’imprimante et le mode d’impression. Vous pouvez réimprimer depuis la vente si nécessaire.',
      },
      {
        title: 'La caisse me ramène à l’ouverture',
        text: 'La session a probablement été fermée ou la reprise a expiré. Demandez au gérant si vous devez rouvrir ou reprendre une journée.',
      },
    ],
  },
];
